import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Save, Trash2, Eye, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import type { ScriptTemplate, ScriptTemplateInput } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { scriptsApi, settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { useCan } from '@/features/auth/store';

const EMPTY: ScriptTemplateInput = {
  name: '',
  language: 'pt-BR',
  firstMessage: '',
  systemPrompt: '',
  isDefault: false,
  voiceId: null,
};

export function ScriptsPage() {
  const qc = useQueryClient();
  const canEdit = useCan('scripts.manage');
  const canDelete = useCan('scripts.delete');
  const list = useQuery({ queryKey: ['scripts'], queryFn: () => scriptsApi.list() });
  const variables = useQuery({ queryKey: ['scripts', 'variables'], queryFn: scriptsApi.variables });
  const languages = useQuery({ queryKey: ['languages'], queryFn: settingsApi.languages });
  const voices = useQuery({
    queryKey: ['voices'],
    queryFn: () => settingsApi.voices(),
    retry: false,
  });

  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ScriptTemplateInput>(EMPTY);
  const [preview, setPreview] = useState<{
    systemPrompt: string;
    firstMessage: string;
    missing: string[];
  } | null>(null);

  const selected = list.data?.find((s) => s.id === selectedId);
  useEffect(() => {
    if (selectedId === 'new') setDraft(EMPTY);
    else if (selected)
      setDraft({
        name: selected.name,
        language: selected.language,
        firstMessage: selected.firstMessage,
        systemPrompt: selected.systemPrompt,
        isDefault: selected.isDefault,
        voiceId: selected.voiceId,
      });
  }, [selectedId, selected]);
  useEffect(() => {
    if (!selectedId && list.data?.length) setSelectedId(list.data[0]!.id);
  }, [list.data, selectedId]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['scripts'] });
  const save = useMutation({
    mutationFn: () =>
      selectedId === 'new' || !selectedId
        ? scriptsApi.create(draft)
        : scriptsApi.update(selectedId, draft),
    onSuccess: (s) => {
      toast.success('Script salvo');
      invalidate();
      setSelectedId(s.id);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => scriptsApi.remove(id),
    onSuccess: () => {
      toast.success('Script excluído');
      invalidate();
      setSelectedId(null);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao excluir'),
  });
  const doPreview = useMutation({
    mutationFn: () => scriptsApi.preview(selectedId!),
    onSuccess: setPreview,
    onError: () => toast.error('Salve o script antes de visualizar'),
  });

  const insertVar = (v: string) =>
    setDraft((d) => ({ ...d, systemPrompt: `${d.systemPrompt}{{${v}}}` }));

  return (
    <div>
      <PageHeader
        title="Scripts"
        description="O que a IA fala em cada idioma. Use variáveis {{assim}} para personalizar por lead."
        actions={
          canEdit && (
            <Button onClick={() => setSelectedId('new')}>
              <Plus /> Novo script
            </Button>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit">
          <CardContent className="p-2">
            {list.isLoading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : list.data?.length ? (
              <ul className="space-y-1">
                {list.data.map((s: ScriptTemplate) => (
                  <li key={s.id}>
                    <button
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted',
                        selectedId === s.id && 'bg-muted font-medium',
                      )}
                      onClick={() => setSelectedId(s.id)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="truncate">{s.name}</span>
                        {s.isDefault && (
                          <Star className="size-3 shrink-0 fill-primary text-primary" />
                        )}
                      </div>
                      <Badge variant="secondary" className="mt-1 text-[10px]">
                        {s.language}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={FileText} title="Nenhum script" />
            )}
          </CardContent>
        </Card>

        {selectedId ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                {selectedId === 'new' ? 'Novo script' : 'Editar script'}
              </CardTitle>
              <div className="flex gap-2">
                {selectedId !== 'new' && (
                  <Button variant="outline" size="sm" onClick={() => doPreview.mutate()}>
                    <Eye /> Preview
                  </Button>
                )}
                {canDelete && selectedId !== 'new' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => confirm('Excluir script?') && remove.mutate(selectedId)}
                  >
                    <Trash2 />
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => save.mutate()}
                    disabled={
                      save.isPending ||
                      !draft.name ||
                      !draft.firstMessage ||
                      draft.systemPrompt.length < 10
                    }
                  >
                    {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Salvar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Nome</Label>
                  <Input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Idioma</Label>
                  <Select
                    value={draft.language}
                    onValueChange={(v) => setDraft({ ...draft, language: v as never })}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.data?.map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Voz (opcional)</Label>
                  <Select
                    value={draft.voiceId ?? '__auto__'}
                    onValueChange={(v) =>
                      setDraft({ ...draft, voiceId: v === '__auto__' ? null : v })
                    }
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Automática para o idioma" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Automática para o idioma</SelectItem>
                      {voices.data?.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name} · {v.gender ?? ''} {v.accent ? `· ${v.accent}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {voices.isError && (
                    <p className="text-xs text-muted-foreground">
                      Configure o provedor para listar vozes.
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <Label htmlFor="default">Padrão do idioma</Label>
                  <Switch
                    id="default"
                    checked={draft.isDefault ?? false}
                    onCheckedChange={(v) => setDraft({ ...draft, isDefault: v })}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Primeira frase (a IA começa falando)</Label>
                <Textarea
                  rows={2}
                  value={draft.firstMessage}
                  onChange={(e) => setDraft({ ...draft, firstMessage: e.target.value })}
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Instruções (system prompt)</Label>
                  <div className="flex flex-wrap gap-1">
                    {variables.data?.map((v) => (
                      <button
                        key={v}
                        type="button"
                        className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] hover:bg-muted/70"
                        onClick={() => insertVar(v)}
                        disabled={!canEdit}
                      >
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea
                  rows={18}
                  className="font-mono text-xs leading-relaxed"
                  value={draft.systemPrompt}
                  onChange={(e) => setDraft({ ...draft, systemPrompt: e.target.value })}
                  disabled={!canEdit}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <EmptyState icon={FileText} title="Selecione ou crie um script" />
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview com dados de exemplo</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              {preview.missing.length > 0 && (
                <p className="rounded bg-warning/20 p-2 text-xs">
                  Variáveis sem valor: {preview.missing.join(', ')}
                </p>
              )}
              <div>
                <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  Primeira frase
                </p>
                <p className="rounded bg-primary/10 p-3">{preview.firstMessage}</p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                  Instruções
                </p>
                <pre className="whitespace-pre-wrap rounded bg-muted p-3 font-sans text-xs leading-relaxed">
                  {preview.systemPrompt}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
